import { useEffect, useState } from "react";
import { Loader2, MessageSquare, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { invoiceAiListSessions, invoiceAiDeleteSession } from "@/lib/api";

function formatWhen(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

export default function InvoiceAiHistoryDialog({ open, onOpenChange, currentSessionId, onSelect, onDeletedCurrent }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    invoiceAiListSessions()
      .then((res) => setSessions(res.sessions || []))
      .catch(() => toast.error("Could not load conversation history."))
      .finally(() => setLoading(false));
  }, [open]);

  const handleDelete = async (e, sessionId) => {
    e.stopPropagation();
    setDeletingId(sessionId);
    try {
      await invoiceAiDeleteSession(sessionId);
      setSessions((s) => s.filter((x) => x.session_id !== sessionId));
      if (sessionId === currentSessionId) onDeletedCurrent?.();
      toast.success("Conversation deleted.");
    } catch {
      toast.error("Could not delete that conversation.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[75vh] overflow-y-auto" data-testid="invoice-ai-history-dialog">
        <DialogTitle>Conversation History</DialogTitle>
        <DialogDescription className="-mt-1 mb-2">
          Past AI Assistant conversations — kept indefinitely. Click one to reopen it.
        </DialogDescription>
        {loading ? (
          <Loader2 className="animate-spin text-[#3A7A8C]" size={20} />
        ) : sessions.length === 0 ? (
          <p className="text-sm text-[#9CA3AF]" data-testid="invoice-ai-history-empty">No past conversations yet.</p>
        ) : (
          <div className="flex flex-col gap-1.5">
            {sessions.map((s) => (
              <div
                key={s.session_id}
                role="button"
                tabIndex={0}
                onClick={() => onSelect(s.session_id)}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onSelect(s.session_id)}
                data-testid={`invoice-ai-history-item-${s.session_id}`}
                className={
                  "text-left border rounded-md px-3 py-2.5 hover:border-[#3A7A8C] transition-colors flex items-start gap-2.5 cursor-pointer " +
                  (s.session_id === currentSessionId ? "border-[#5FA8B4] bg-[#5FA8B4]/5" : "border-[#E5E7EB]")
                }
              >
                <MessageSquare size={14} className="text-[#3A7A8C] shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#080808] font-medium truncate">
                    {s.bill_to_name || s.snippet || "New conversation"}
                    {s.invoice_number ? ` · ${s.invoice_number}` : ""}
                  </p>
                  <p className="text-xs text-[#9CA3AF]">
                    {formatWhen(s.updated_at)} · {s.message_count} messages · {s.workflow_status}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={(e) => handleDelete(e, s.session_id)}
                  disabled={deletingId === s.session_id}
                  data-testid={`invoice-ai-history-delete-${s.session_id}`}
                  className="shrink-0 text-[#9CA3AF] hover:text-red-500 disabled:opacity-50 p-1"
                  title="Delete this conversation"
                >
                  {deletingId === s.session_id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                </button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
